// Security update: HTML rendered from stored content is sanitized with sanitizeHtml() before it is inserted into the page.
/* Klantenservice management page in the shared admin page frame. */



import { sanitizeHtml } from '@/lib/sanitize-html';
import { useState, useEffect, useRef } from 'react';

import Breadcrumb from '@/components/admin/Breadcrumb';
import SimpleMarkdownToolbar from '@/components/admin/SimpleMarkdownToolbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, CreditCard as Edit2, Trash2, CircleAlert as AlertCircle, CircleCheck as CheckCircle2, CircleHelp as HelpCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { marked } from 'marked';

interface KlantenservicePage {
  id: string;
  slug: string;
  title: string;
  description: string;
  display_order: number;
  active: boolean;
}

interface FAQ {
  id: string;
  page_id: string;
  question: string;
  answer: string;
  display_order: number;
  active: boolean;
}

export default function KlantenserviceAdminPage() {
  const [pages, setPages] = useState<KlantenservicePage[]>([]);
  const [selectedPage, setSelectedPage] = useState<KlantenservicePage | null>(null);
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFAQDialogOpen, setIsFAQDialogOpen] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState<FAQ | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const [faqForm, setFaqForm] = useState({
    question: '',
    answer: '',
    active: true
  });

  const answerTextareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    fetchPages();
  }, []);

  useEffect(() => {
    if (selectedPage) {
      fetchFAQs(selectedPage.id);
    }
  }, [selectedPage]);

  const fetchPages = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from('klantenservice_pages')
      .select('*')
      .order('display_order');

    if (error) {
      setMessage({ type: 'error', text: 'Fout bij laden van paginas' });
    } else if (data) {
      const filtered = data.filter(p => p.slug !== 'aanplantservice');
      setPages(filtered);
      if (!selectedPage && filtered.length > 0) {
        setSelectedPage(filtered[0]);
      }
    }
    setIsLoading(false);
  };

  const fetchFAQs = async (pageId: string) => {
    const { data, error } = await supabase
      .from('klantenservice_faqs')
      .select('*')
      .eq('page_id', pageId)
      .order('display_order');

    if (error) {
      setMessage({ type: 'error', text: 'Fout bij laden van FAQs' });
    } else if (data) {
      setFaqs(data);
    }
  };

  const handleAddFAQ = () => {
    setEditingFAQ(null);
    setFaqForm({ question: '', answer: '', active: true });
    setIsFAQDialogOpen(true);
  };

  const handleEditFAQ = (faq: FAQ) => {
    setEditingFAQ(faq);
    setFaqForm({
      question: faq.question,
      answer: faq.answer,
      active: faq.active
    });
    setIsFAQDialogOpen(true);
  };

  const handleSaveFAQ = async () => {
    if (!selectedPage || !faqForm.question || !faqForm.answer) {
      setMessage({ type: 'error', text: 'Vraag en antwoord zijn verplicht' });
      return;
    }

    const faqData = {
      page_id: selectedPage.id,
      question: faqForm.question,
      answer: faqForm.answer,
      active: faqForm.active,
      display_order: editingFAQ ? editingFAQ.display_order : faqs.length
    };

    if (editingFAQ) {
      const { error } = await supabase
        .from('klantenservice_faqs')
        .update(faqData)
        .eq('id', editingFAQ.id);

      if (error) {
        setMessage({ type: 'error', text: 'Fout bij bijwerken van FAQ' });
      } else {
        setMessage({ type: 'success', text: 'FAQ succesvol bijgewerkt' });
        fetchFAQs(selectedPage.id);
        setIsFAQDialogOpen(false);
      }
    } else {
      const { error } = await supabase
        .from('klantenservice_faqs')
        .insert([faqData]);

      if (error) {
        setMessage({ type: 'error', text: 'Fout bij toevoegen van FAQ' });
      } else {
        setMessage({ type: 'success', text: 'FAQ succesvol toegevoegd' });
        fetchFAQs(selectedPage.id);
        setIsFAQDialogOpen(false);
      }
    }
  };

  const handleDeleteFAQ = async (faqId: string) => {
    if (!confirm('Weet je zeker dat je deze FAQ wilt verwijderen?')) return;

    const { error } = await supabase
      .from('klantenservice_faqs')
      .delete()
      .eq('id', faqId);

    if (error) {
      setMessage({ type: 'error', text: 'Fout bij verwijderen van FAQ' });
    } else {
      setMessage({ type: 'success', text: 'FAQ succesvol verwijderd' });
      if (selectedPage) {
        fetchFAQs(selectedPage.id);
      }
    }
  };

  const handleToggleFAQActive = async (faq: FAQ) => {
    const { error } = await supabase
      .from('klantenservice_faqs')
      .update({ active: !faq.active })
      .eq('id', faq.id);

    if (error) {
      setMessage({ type: 'error', text: 'Fout bij bijwerken van FAQ status' });
    } else {
      if (selectedPage) {
        fetchFAQs(selectedPage.id);
      }
    }
  };

  const handleAnswerToolbarInsert = (before: string, after?: string) => {
    const textarea = answerTextareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = faqForm.answer.substring(start, end);

    const newText = before + selectedText + (after || '');
    const newAnswer =
      faqForm.answer.substring(0, start) +
      newText +
      faqForm.answer.substring(end);

    setFaqForm({ ...faqForm, answer: newAnswer });

    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + before.length + selectedText.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  return (
    <>
      <div className="max-w-7xl mx-auto px-6 py-12">
        <Breadcrumb pageName="Klantenservice" />

        <div className="mb-8">
          <h1 className="text-4xl font-bold text-jv-primary mb-2">Klantenservice</h1>
          <p className="text-gray-600">Beheer de klantenservice paginas en hun FAQ inhoud</p>
        </div>

        {message && (
          <Alert variant={message.type === 'error' ? 'destructive' : 'default'} className="mb-6">
            {message.type === 'success' ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
            <AlertDescription>{message.text}</AlertDescription>
          </Alert>
        )}

        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
          <CardContent className="pt-6">
            <Tabs value={selectedPage?.id} onValueChange={(value) => {
              const page = pages.find(p => p.id === value);
              if (page) setSelectedPage(page);
            }}>
              <TabsList className={`grid w-full ${pages.length >= 5 ? 'grid-cols-5' : `grid-cols-${pages.length}`}`}>
                {pages.map((page) => (
                  <TabsTrigger key={page.id} value={page.id}>
                    {page.title}
                  </TabsTrigger>
                ))}
              </TabsList>

              {pages.map((page) => (
                <TabsContent key={page.id} value={page.id} className="space-y-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-semibold">{page.title}</h3>
                      <p className="text-sm text-gray-500">{page.description}</p>
                      <p className="text-xs text-gray-400 mt-1">Slug: /klantenservice/{page.slug}</p>
                    </div>
                    <Button onClick={handleAddFAQ} className="bg-jv-primary hover:bg-jv-primary/90">
                      <Plus className="w-4 h-4 mr-2" />
                      FAQ Toevoegen
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {faqs.length === 0 ? (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <HelpCircle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                        <p className="text-gray-500">Nog geen FAQs toegevoegd</p>
                      </div>
                    ) : (
                      faqs.map((faq) => (
                        <Card key={faq.id}>
                          <CardContent className="p-6">
                            <div className="flex justify-between items-start gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h4 className="font-semibold text-lg">{faq.question}</h4>
                                  {!faq.active && (
                                    <span className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded">
                                      Inactief
                                    </span>
                                  )}
                                </div>
                                <div
                                  className="text-gray-600 prose prose-sm max-w-none"
                                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(marked.parse(faq.answer) as string) }}
                                />
                              </div>
                              <div className="flex gap-2">
                                <Switch
                                  checked={faq.active}
                                  onCheckedChange={() => handleToggleFAQActive(faq)}
                                />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleEditFAQ(faq)}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleDeleteFAQ(faq.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </div>
      </div>

      {/* FAQ Dialog */}
      <Dialog open={isFAQDialogOpen} onOpenChange={setIsFAQDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingFAQ ? 'FAQ Bewerken' : 'Nieuwe FAQ Toevoegen'}
            </DialogTitle>
            <DialogDescription>
              {selectedPage && `Voor pagina: ${selectedPage.title}`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="question">Vraag *</Label>
              <Input
                id="question"
                value={faqForm.question}
                onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })}
                placeholder="Bijv: Welke betaalmethoden accepteren jullie?"
              />
            </div>

            <div>
              <Label htmlFor="answer">Antwoord * (ondersteunt markdown)</Label>
              <SimpleMarkdownToolbar onInsert={handleAnswerToolbarInsert} />
              <Textarea
                ref={answerTextareaRef}
                id="answer"
                value={faqForm.answer}
                onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })}
                placeholder="Geef hier een uitgebreid antwoord in markdown formaat..."
                rows={8}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="active"
                checked={faqForm.active}
                onCheckedChange={(checked) => setFaqForm({ ...faqForm, active: checked })}
              />
              <Label htmlFor="active">Actief</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsFAQDialogOpen(false)}>
              Annuleren
            </Button>
            <Button onClick={handleSaveFAQ} className="bg-jv-primary hover:bg-jv-primary/90">
              {editingFAQ ? 'Bijwerken' : 'Toevoegen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
